import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import {
  fetchPayrollOptions,
  HeaderScan,
  PayrollOptions,
  PayrollRunSummary,
  runPayrollAgent,
  scanPayrollHeaders,
} from '../../api/batch10Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload payroll PDFs and/or payroll Excel files, plus the nominal ledger (Xero or Sage export) and optionally a trial balance.',
  'Leave parsing on Automatic, or choose Manual column mapping for a PDF layout the auto-detection gets wrong.',
  'Click Generate Reconciliation. This can take up to a minute.',
];

const LEAVE_EMPTY = '';
const TYPE_MANUALLY = '__manual__';
const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function PayrollAgentPanel({ mission, onBack }: PanelProps) {
  const [options, setOptions] = useState<PayrollOptions | null>(null);
  const [parseMode, setParseMode] = useState<'auto' | 'manual'>('auto');
  const [format, setFormat] = useState('auto');
  const [pdfs, setPdfs] = useState<File[]>([]);
  const [excels, setExcels] = useState<File[]>([]);
  const [nominal, setNominal] = useState<File[]>([]);
  const [tb, setTb] = useState<File[]>([]);
  const [directors, setDirectors] = useState('');

  const [scan, setScan] = useState<HeaderScan | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [typed, setTyped] = useState<Record<string, string>>({});

  const { status, error, result, run } = useFileRun<FileResult<PayrollRunSummary>>();

  useEffect(() => {
    fetchPayrollOptions().then(setOptions).catch(() => setOptions(null));
  }, []);

  // In manual mode, read the column headers of the uploaded files and pre-select likely matches.
  useEffect(() => {
    setScan(null);
    setScanError(null);
    if (parseMode !== 'manual' || (pdfs.length === 0 && excels.length === 0)) return;
    let cancelled = false;
    scanPayrollHeaders(pdfs, excels)
      .then((data) => {
        if (cancelled) return;
        setScan(data);
        setChoice(
          Object.fromEntries(Object.entries(data.suggestions).map(([key, value]) => [key, value || LEAVE_EMPTY]))
        );
        setTyped({});
      })
      .catch((err) => !cancelled && setScanError(err instanceof Error ? err.message : 'Could not scan the files.'));
    return () => {
      cancelled = true;
    };
  }, [parseMode, pdfs, excels]);

  const manualMap = (): Record<string, string> => {
    const out: Record<string, string> = {};
    (options?.fields ?? []).forEach(({ key }) => {
      const picked = choice[key] ?? LEAVE_EMPTY;
      if (picked === TYPE_MANUALLY) {
        if ((typed[key] ?? '').trim()) out[key] = typed[key].trim();
      } else if (picked !== LEAVE_EMPTY) {
        out[key] = picked;
      }
    });
    return out;
  };

  const handleSubmit = async () => {
    if (pdfs.length === 0 && excels.length === 0) return;
    await run(() => runPayrollAgent({
      pdfs,
      excels,
      nominal: nominal[0] ?? null,
      trialBalance: tb[0] ?? null,
      directors,
      parseMode,
      format,
      manualMap: parseMode === 'manual' ? manualMap() : {},
    }), 'Processing failed.');
  };

  const formulaValue = options?.formula_value ?? '__formula__';
  const columns = scan?.pdf.columns.length ? scan.pdf.columns : [];
  const noInput = pdfs.length === 0 && excels.length === 0;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 grid gap-4 rounded-lg border border-border bg-muted p-4 sm:grid-cols-2">
        <div>
          <p className="mb-1.5 text-sm font-medium text-foreground">Parsing method</p>
          <div className="flex gap-2">
            {([['auto', 'Automatic'], ['manual', 'Manual column mapping']] as ['auto' | 'manual', string][]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setParseMode(key)}
                className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                  parseMode === key ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="pa-format" className="mb-1.5">
            PDF format mode
          </Label>
          <select id="pa-format" className={selectClass} value={format} onChange={(e) => setFormat(e.target.value)}>
            {(options?.formats ?? [{ key: 'auto', label: 'Auto detect (recommended)' }]).map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">Use Auto detect normally. Pick a format only if a PDF is mapped incorrectly.</p>
        </div>
      </div>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <FileDrop label="Payroll PDF(s)" multiple accept=".pdf" hint="PDF" accentColor={mission.color} files={pdfs} onFiles={setPdfs} />
        <FileDrop label="Nominal Ledger (.xlsx)" note="Xero or Sage nominal activity export." accept=".xlsx" hint="XLSX" accentColor={mission.color} files={nominal} onFiles={setNominal} />
        <FileDrop label="Payroll Excel(s)" multiple accept=".xlsx" hint="XLSX" accentColor={mission.color} files={excels} onFiles={setExcels} />
        <FileDrop label="Trial Balance (.xlsx), optional" note="Used to auto-identify payroll control account names." accept=".xlsx" hint="XLSX" accentColor={mission.color} files={tb} onFiles={setTb} />
      </div>

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="pa-directors" className="mb-1.5">
          Director names (optional)
        </Label>
        <Input id="pa-directors" value={directors} onChange={(e) => setDirectors(e.target.value)} placeholder="e.g. John Smith, Jane Doe" />
        <p className="mt-1 text-xs text-muted-foreground">Comma-separated, for Director Remuneration matching.</p>
      </div>

      {parseMode === 'manual' && (
        <div className="mb-5 space-y-4">
          <InfoNotice>
            Manual mode applies to all uploaded PDFs. It tries manual table mapping first, then falls back to line-based
            parsing when table/header extraction fails.
          </InfoNotice>
          {scanError && <ErrorNotice message={scanError} />}
          {scan && columns.length === 0 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Could not detect header rows. Manual mode can still run using line-based fallback.
            </p>
          )}
          {scan && columns.length > 0 && (
            <>
              <div>
                <p className="mb-1 text-sm font-semibold text-foreground">Detected {columns.length} PDF columns</p>
                <div className="max-h-52 overflow-auto rounded-md border border-border">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="px-3 py-1.5 font-semibold">Column</th>
                        <th className="px-3 py-1.5 font-semibold">Sample value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scan.pdf.samples.map((s) => (
                        <tr key={s.column} className="border-t border-border">
                          <td className="px-3 py-1.5">{s.column}</td>
                          <td className="px-3 py-1.5">{s.sample}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="space-y-2">
                {options?.fields.map((field) => (
                  <div key={field.key} className="grid items-center gap-2 sm:grid-cols-[200px_1fr]">
                    <div>
                      <span className="text-sm font-semibold text-foreground">{field.label}</span>
                      {!field.required && <span className="ml-1 text-xs text-muted-foreground">(optional)</span>}
                      <p className="text-xs text-muted-foreground">Detected: {scan.suggestions[field.key] || 'Not found'}</p>
                    </div>
                    <div className="space-y-1.5">
                      <select
                        className={selectClass}
                        aria-label={`${field.label} source column`}
                        value={choice[field.key] ?? LEAVE_EMPTY}
                        onChange={(e) => setChoice((c) => ({ ...c, [field.key]: e.target.value }))}
                      >
                        <option value={LEAVE_EMPTY}>(leave empty)</option>
                        {columns.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                        <option value={TYPE_MANUALLY}>(type manually)</option>
                        {field.formula && <option value={formulaValue}>(use formula)</option>}
                      </select>
                      {field.formula && choice[field.key] === formulaValue && (
                        <p className="text-xs text-muted-foreground">EE Pension = Net Pay − Take-Home Pay</p>
                      )}
                      {choice[field.key] === TYPE_MANUALLY && (
                        <Input
                          value={typed[field.key] ?? ''}
                          onChange={(e) => setTyped((t) => ({ ...t, [field.key]: e.target.value }))}
                          placeholder="Enter exact header text from the PDF"
                          aria-label={`Type ${field.label} source column`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {manualMap().ee_pension === formulaValue && !(manualMap().net_pay && manualMap().take_home) && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  EE Pension is set to formula (Net Pay − Take-Home Pay), so map both Net Pay and Take-Home Pay —
                  otherwise EE Pension will come out as 0.
                </p>
              )}
              {Object.keys(manualMap()).length === 0 && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  No manual mapping selected. The run will use the manual line-based fallback for the PDFs.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={noInput || status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Processing…' : 'Generate Reconciliation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Processing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ Reconciliation complete. {result.filename} downloaded.</div>
          {result.summary?.log && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">Processing log</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{result.summary.log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && noInput && <InfoNotice>Upload payroll PDF(s) and/or payroll Excel file(s) to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default PayrollAgentPanel;
